package dev.kuml.portal.web.error

import dev.kuml.portal.web.i18n.strings.ErrorStrings

/**
 * Status-area copy for the KI-generation flow's error/quota states (design spec,
 * "Fehler-/Kontingent-Zustaende"). Pure function, DOM-free, so the exact copy per status code is
 * directly unit-testable (see `ErrorCopyTest`) rather than only checkable by eye.
 *
 * R-18, decision E-9: the server's `ErrorResponse.message` is NEVER rendered anymore. This
 * function now takes the active language's [ErrorStrings] and picks copy either by [serverCode]
 * (via [ErrorStrings.forCode], the server-issued machine-readable code -- see this wave's plan
 * §7) or by [status] alone for the well-known generation-flow statuses below. The ONE exception is
 * 422's [detail] line: the kUML compiler's own diagnostic text in [serverMessage] is untranslatable
 * (it names DSL symbols) and is shown verbatim, prefixed with [ErrorStrings.compilerDetailPrefix]
 * so it reads as machine output, not UI copy.
 */
public object ErrorCopy {
    /**
     * @property message the primary status-area text.
     * @property detail an optional secondary line (422's compiler diagnostic, prefixed and shown
     *   de-emphasized).
     * @property disablesGenerateButton whether "Diagramm generieren" should stay disabled for the
     *   rest of the period (402 only).
     * @property isRetryable whether a visible "Erneut versuchen" affordance applies (429's
     *   auto-reactivating countdown, or a network/unknown error's explicit retry button).
     */
    public data class Copy(
        val message: String,
        val detail: String? = null,
        val disablesGenerateButton: Boolean = false,
        val isRetryable: Boolean = false,
    )

    /**
     * @param status the HTTP status code of the failed `POST .../ai/generate` (or `/render`) call.
     * @param serverCode the server's `ErrorResponse.code` (see this wave's plan §7.3's table) --
     *   used FIRST, ahead of the generic per-status copy below, whenever it maps to a known
     *   [ErrorStrings.forCode] entry (e.g. `diagram_live_room`, `ai_context_unavailable`). `null`
     *   for statuses that never carry a specific code.
     * @param serverMessage the server's `ErrorResponse.message` -- used ONLY as 422's compiler
     *   diagnostic detail line (see class KDoc), never as primary copy anywhere.
     * @param retryAfterSeconds parsed `Retry-After` header, for 429.
     * @param aiLimit the caller's current AI-generation limit (hard or soft, whichever applies),
     *   for 402's "(X/X)" fragment -- `null` omits the fragment entirely.
     * @param resetLabel the already-locale-formatted reset date for 402 (compute via
     *   `dev.kuml.portal.web.usage.UsageFormat.nextPeriodStartLabel`), `null` if unknown.
     */
    public fun forHttpStatus(
        status: Int,
        serverCode: String?,
        serverMessage: String?,
        retryAfterSeconds: Long?,
        aiLimit: Int?,
        resetLabel: String?,
        strings: ErrorStrings,
    ): Copy {
        val fromCode = strings.forCode(serverCode, status)
        return when (status) {
            401 -> Copy(message = strings.sessionExpired)
            402 -> Copy(message = strings.quotaExhausted(aiLimit, resetLabel), disablesGenerateButton = true)
            404 -> Copy(message = fromCode ?: strings.projectNotFound)
            422 ->
                Copy(
                    message = strings.validationRejected,
                    detail = serverMessage?.let { "${strings.compilerDetailPrefix} $it" },
                )
            429 -> Copy(message = strings.rateLimited(retryAfterSeconds), isRetryable = true)
            503 -> Copy(message = fromCode ?: strings.serviceUnavailable)
            409 -> Copy(message = fromCode ?: strings.networkError)
            400 -> if (fromCode != null) Copy(message = fromCode) else forNetworkError(strings)
            else -> forNetworkError(strings)
        }
    }

    public fun forNetworkError(strings: ErrorStrings): Copy = Copy(message = strings.networkError, isRetryable = true)
}
